import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { format } from 'date-fns';
import { useCurrency } from '../store/currency';
import CurrencyInput from '../components/CurrencyInput';
import { useIsMobile } from '../hooks/useIsMobile';

const today = () => new Date().toISOString().split('T')[0];

//  Split Modal 
function SplitModal({ counterparts, onClose, onSaved, fmt }) {
  const displayCurrency = localStorage.getItem('display_currency') || 'USD';
  const symbols = {"GTQ": "Q", "USD": "$", "EUR": "€", "GBP": "£"};
  const symbol = symbols[displayCurrency] || displayCurrency + " ";
  const [date, setDate] = useState(today());
  const [description, setDescription] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [usdTotal, setUsdTotal] = useState(0);
  const [splitType, setSplitType] = useState('equal'); // equal | custom
  const [participants, setParticipants] = useState([]); // [{ id, name, share, custom }]
  const [saving, setSaving] = useState(false);

  const totalUSD = usdTotal > 0 ? usdTotal : parseFloat(totalAmount) || 0;
  const numPeople = participants.length;
  const equalShare = numPeople > 0 ? totalUSD / numPeople : 0;

  const toggleParticipant = (p) => {
    setParticipants(prev => {
      const exists = prev.find(x => x.id === p.id);
      if (exists) return prev.filter(x => x.id !== p.id);
      return [...prev, { id: p.id, name: p.name, custom: '' }];
    });
  };

  const setCustomShare = (id, val) => {
    setParticipants(prev => prev.map(p => p.id === id ? { ...p, custom: val } : p));
  };

  const customTotal = participants.reduce((a, p) => a + (parseFloat(p.custom) || 0), 0);
  const customRemaining = Math.round((totalUSD - customTotal) * 100) / 100;

  const getShare = (p) => splitType === 'equal' ? equalShare : (parseFloat(p.custom) || 0);

  const save = async () => {
    if (!description.trim()) { toast.error('Add a description'); return; }
    if (totalUSD <= 0) { toast.error('Enter a total amount'); return; }
    if (participants.length === 0) { toast.error('Select at least one person'); return; }
    if (splitType === 'custom' && customRemaining !== 0) {
      toast.error(`Custom shares must add up to total. Remaining: $${customRemaining}`); return;
    }

    setSaving(true);
    try {
      const displayCurrency = localStorage.getItem('display_currency') || 'USD';
      await Promise.all(participants.map(p => api.createIouEntry({
        counterpart_id: p.id,
        date,
        description,
        amount: Math.round(getShare(p) * 100) / 100,
        original_amount: Math.round(getShare(p) * 100) / 100,
        original_currency: displayCurrency,
        notes: `Split ${participants.length + 1} ways — total ${symbol}${totalUSD.toFixed(2)}`,
      })));
      toast.success(`Split created — ${participants.length} entries added`);
      onSaved();
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-title">Split Split an Expense</div>
        <div style={{ fontSize: 12, color: 'var(--mid)', marginBottom: 16 }}>
          Enter the total, choose who's involved, and Arca creates IOU entries for each person's share.
        </div>

        {/* Total + description */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date</label>
            <input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Total Amount *</label>
            <CurrencyInput
              value={totalAmount}
              onChange={(raw, usd) => { setTotalAmount(raw); setUsdTotal(usd); }}
              placeholder="0.00"
            />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Description *</label>
          <input className="input" placeholder="e.g. House worker salary, Dinner, Groceries" value={description} onChange={e => setDescription(e.target.value)} />
        </div>

        {/* Who's involved */}
        <div className="form-group">
          <label className="form-label">Who's involved? (select people who owe you)</label>
          {counterparts.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--mid)', padding: '10px 0' }}>
              No people yet — add someone first from the main IOU page.
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {counterparts.map(p => {
                const selected = !!participants.find(x => x.id === p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggleParticipant(p)}
                    style={{
                      padding: '7px 14px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
                      border: `1.5px solid ${selected ? 'var(--accent)' : 'var(--stone)'}`,
                      background: selected ? '#eef3ff' : 'white',
                      color: selected ? 'var(--accent)' : 'var(--ink2)',
                      fontWeight: selected ? 600 : 400,
                      transition: 'all 0.1s',
                    }}
                  >
                    {selected ? ' ' : ''}{p.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Split type */}
        {participants.length > 0 && totalUSD > 0 && (
          <>
            <div className="form-group">
              <label className="form-label">Split type</label>
              <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--stone)', width: 'fit-content' }}>
                {[['equal', ' Equal'], ['custom', 'Edit Custom']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setSplitType(val)} style={{
                    padding: '7px 16px', fontSize: 12.5, border: 'none', cursor: 'pointer',
                    background: splitType === val ? 'var(--ink)' : 'white',
                    color: splitType === val ? 'white' : 'var(--ink2)',
                    fontWeight: splitType === val ? 600 : 400,
                  }}>{label}</button>
                ))}
              </div>
            </div>

            {/* Share breakdown */}
            <div style={{ background: 'var(--off)', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)', marginBottom: 10 }}>
                Breakdown — {participants.length + 1} people total (including you)
              </div>

              {/* Your share */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--stone)', marginBottom: 6 }}>
                <span style={{ fontSize: 13, color: 'var(--mid)', fontStyle: 'italic' }}>Your share (not tracked)</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--mid)' }}>
                  ${splitType === 'equal' ? equalShare.toFixed(2) : Math.max(0, customRemaining).toFixed(2)}
                </span>
              </div>

              {participants.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--stone)' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{p.name} owes you</span>
                  {splitType === 'equal' ? (
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>${equalShare.toFixed(2)}</span>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--mid)' }}>$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={p.custom}
                        onChange={e => setCustomShare(p.id, e.target.value)}
                        placeholder="0.00"
                        style={{ width: 80, padding: '4px 8px', fontSize: 12.5, border: '1px solid var(--stone)', borderRadius: 5, textAlign: 'right' }}
                      />
                    </div>
                  )}
                </div>
              ))}

              {splitType === 'custom' && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', fontSize: 12 }}>
                  <span style={{ color: 'var(--mid)' }}>Remaining (your share)</span>
                  <span style={{ fontWeight: 600, color: customRemaining < 0 ? 'var(--red)' : 'var(--ink)' }}>
                    ${customRemaining.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex gap-3 justify-between">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving || participants.length === 0 || totalUSD <= 0}>
            {saving ? 'Creating entries…' : `Split Split — Create ${participants.length} Entr${participants.length === 1 ? 'y' : 'ies'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

//  Main Page 
export default function IouPage() {
  const { fmt } = useCurrency();
  const isMobile = useIsMobile();
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showAddPerson, setShowAddPerson] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showSplit, setShowSplit] = useState(false);
  const [loading, setLoading] = useState(true);

  const [personForm, setPersonForm] = useState({ name: '', email: '', notes: '' });
  const [entryForm, setEntryForm] = useState({ counterpart_id: '', date: today(), description: '', amount: '', direction: 'they_owe_me', notes: '' });
  const [usdAmount, setUsdAmount] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [sum, ents] = await Promise.all([api.getIouSummary(), api.getIouEntries()]);
      setSummary(sum);
      setEntries(ents);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const addPerson = async () => {
    if (!personForm.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await api.createIouCounterpart(personForm);
      setPersonForm({ name: '', email: '', notes: '' });
      setShowAddPerson(false);
      toast.success('Person added!');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const addEntry = async () => {
    if (!entryForm.counterpart_id || !entryForm.description || !entryForm.amount) {
      toast.error('Fill in all required fields'); return;
    }
    setSaving(true);
    try {
      const raw = usdAmount > 0 ? usdAmount : parseFloat(entryForm.amount);
      const displayCurrency = localStorage.getItem('display_currency') || 'USD';
      const amount = entryForm.direction === 'they_owe_me' ? Math.abs(raw) : -Math.abs(raw);
      const origRaw = parseFloat(entryForm.amount) || 0;
      const origAmount = entryForm.direction === 'they_owe_me' ? Math.abs(origRaw) : -Math.abs(origRaw);
      await api.createIouEntry({
        counterpart_id: parseInt(entryForm.counterpart_id),
        date: entryForm.date,
        description: entryForm.description,
        amount,
        original_amount: origAmount,
        original_currency: displayCurrency,
        notes: entryForm.notes || null,
      });
      setEntryForm({ counterpart_id: selectedPerson?.id || '', date: today(), description: '', amount: '', direction: 'they_owe_me', notes: '' });
      setUsdAmount(0);
      setShowAddEntry(false);
      toast.success('Entry added!');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const settle = async (entryId) => {
    try { await api.settleIouEntry(entryId); toast.success('Settled!'); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const unsettle = async (entryId) => {
    try {
      const t = (() => { try { return localStorage.getItem('token'); } catch { return sessionStorage.getItem('token'); } })();
      await fetch(`/api/iou/entries/${entryId}/unsettle`, { method: 'PATCH', headers: { Authorization: `Bearer ${t}` } });
      toast.success('Reopened!'); await load();
    } catch (e) { toast.error('Failed to unsettle'); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try { await api.deleteIouEntry(id); await load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message); }
  };

  const deletePerson = async (id, name) => {
    if (!window.confirm(`Delete ${name} and all their entries?`)) return;
    try { await api.deleteIouCounterpart(id); setSelectedPerson(null); await load(); toast.success('Deleted'); }
    catch (e) { toast.error(e.message); }
  };

  const personEntries = entries.filter(e => selectedPerson ? e.counterpart_id === selectedPerson.id : true);
  const unsettled = personEntries.filter(e => !e.is_settled);
  const settled_list = personEntries.filter(e => e.is_settled);

  return (
    <div className="animate-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="page-eyebrow">Money</div>
          <div className="page-title">IOUs & Split</div>
          <div className="page-subtitle" style={{ marginBottom: 0 }}>Track who owes you and split expenses among multiple people</div>
        </div>
        <div className="flex gap-2" style={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={() => { setShowAddPerson(true); setShowAddEntry(false); setShowSplit(false); }}>+ Person</button>
          <button className="btn btn-ghost" onClick={() => { setShowAddEntry(true); setShowAddPerson(false); setShowSplit(false); }}>+ Entry</button>
          <button className="btn btn-primary" onClick={() => { setShowSplit(true); setShowAddPerson(false); setShowAddEntry(false); }}>Split Split</button>
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid-3 mb-6">
          <div className="card">
            <div className="stat-label">They Owe Me</div>
            <div className="stat-value green">{fmt(summary.owed_to_me)}</div>
            <div className="stat-sub">Outstanding receivables</div>
          </div>
          <div className="card">
            <div className="stat-label">I Owe Them</div>
            <div className="stat-value red">{fmt(summary.i_owe)}</div>
            <div className="stat-sub">Outstanding payables</div>
          </div>
          <div className="card">
            <div className="stat-label">Net Position</div>
            <div className={`stat-value ${summary.net >= 0 ? 'green' : 'red'}`}>{fmt(Math.abs(summary.net))}</div>
            <div className="stat-sub">{summary.net >= 0 ? 'Net owed to you' : 'Net you owe'}</div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '240px 1fr', gap: 20 }}>
        {/* People sidebar */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)', marginBottom: 8 }}>People</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              onClick={() => setSelectedPerson(null)}
              style={{
                padding: '10px 14px', borderRadius: 8, border: '1px solid var(--stone)',
                background: !selectedPerson ? 'var(--ink)' : 'white',
                color: !selectedPerson ? 'white' : 'var(--ink2)',
                cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 500,
              }}
            >
              Everyone · {summary?.counterparts?.length || 0} people
            </button>
            {summary?.counterparts?.map(p => (
              <button key={p.id} onClick={() => setSelectedPerson(p)} style={{
                padding: '10px 14px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
                border: `1px solid ${selectedPerson?.id === p.id ? 'var(--accent)' : 'var(--stone)'}`,
                background: selectedPerson?.id === p.id ? '#eef3ff' : 'white',
                transition: 'all 0.1s',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{p.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: p.balance > 0 ? 'var(--green)' : p.balance < 0 ? 'var(--red)' : 'var(--mid)' }}>
                    {p.balance > 0 ? '+' : ''}{fmt(p.balance)}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 2 }}>
                  {p.balance > 0 ? 'owes you' : p.balance < 0 ? 'you owe' : 'settled up'} · {p.entry_count} open
                </div>
              </button>
            ))}
            {!loading && !summary?.counterparts?.length && (
              <div style={{ fontSize: 12, color: 'var(--mid)', padding: '12px 0', textAlign: 'center' }}>
                No people yet — click "+ Person"
              </div>
            )}
          </div>
        </div>

        {/* Entries panel */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--mid)' }}>
              {selectedPerson ? selectedPerson.name : 'All Entries'}
            </div>
            {selectedPerson && (
              <div className="flex gap-2">
                <button className="btn btn-sm btn-ghost" onClick={() => { setEntryForm(f => ({ ...f, counterpart_id: selectedPerson.id })); setShowAddEntry(true); }}>+ Entry</button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={async () => {
                    try {
                      const t = (() => { try { return localStorage.getItem('token'); } catch { return sessionStorage.getItem('token'); } })();
                      const currency = (() => { try { return localStorage.getItem('display_currency') || 'USD'; } catch { return 'USD'; } })();
                      const res = await fetch(`/api/iou/counterparts/${selectedPerson.id}/summary?currency=${currency}`, {
                        headers: { Authorization: `Bearer ${t}` }
                      });
                      const html = await res.text();
                      const blob = new Blob([html], { type: 'text/html' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.target = '_blank';
                      a.rel = 'noopener noreferrer';
                      document.body.appendChild(a);
                      a.click();
                      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1000);
                    } catch (e) { toast.error('Could not generate statement'); }
                  }}
                >Statement</button>
                <button className="btn btn-sm btn-danger" onClick={() => deletePerson(selectedPerson.id, selectedPerson.name)}>Delete</button>
              </div>
            )}
          </div>

          {/* Unsettled */}
          {unsettled.length > 0 && (
            <div className="card mb-4" style={{ padding: 0 }}>
              <div style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--ink2)', borderBottom: '1px solid var(--stone)' }}>
                Open — {unsettled.length} entries
              </div>
              {unsettled.map((e, i) => {
                const person = summary?.counterparts?.find(p => p.id === e.counterpart_id);
                return (
                  <div key={e.id} style={{
                    display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                    borderBottom: i < unsettled.length - 1 ? '1px solid var(--stone)' : 'none',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>{e.description}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--mid)', marginTop: 2 }}>
                        {!selectedPerson && person && <span style={{ marginRight: 6 }}>{person.name} ·</span>}
                        {format(new Date(e.date), 'MMM d, yyyy')}
                        {e.notes && <span style={{ marginLeft: 6, color: 'var(--stone)' }}>· {e.notes}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: e.amount > 0 ? 'var(--green)' : 'var(--red)' }}>
                        {e.amount > 0 ? '+' : ''}{fmt(e.amount)}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--mid)' }}>{e.amount > 0 ? 'owes you' : 'you owe'}</div>
                    </div>
                    <div className="flex gap-2">
                      <button className="btn btn-sm btn-ghost" style={{ color: 'var(--green)' }} onClick={() => settle(e.id)}> Settle</button>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteEntry(e.id)}></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Settled */}
          {settled_list.length > 0 && (
            <div className="card" style={{ padding: 0, opacity: 0.6 }}>
              <div style={{ padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--mid)', borderBottom: '1px solid var(--stone)' }}>
                Settled — {settled_list.length} entries
              </div>
              {settled_list.map((e, i) => {
                const person = summary?.counterparts?.find(p => p.id === e.counterpart_id);
                return (
                  <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 16px', borderBottom: i < settled_list.length - 1 ? '1px solid var(--stone)' : 'none' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, color: 'var(--mid)', textDecoration: 'line-through' }}>{e.description}</div>
                      <div style={{ fontSize: 11, color: 'var(--mid)', marginTop: 2 }}>
                        {!selectedPerson && person && <span>{person.name} · </span>}
                        Settled {e.settled_date ? format(new Date(e.settled_date), 'MMM d, yyyy') : ''}
                      </div>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--mid)', textDecoration: 'line-through' }}>{fmt(Math.abs(e.amount))}</div>
                    <button className="btn btn-sm btn-ghost" style={{ fontSize: 11 }} onClick={() => unsettle(e.id)}>Reopen</button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteEntry(e.id)}>×</button>
                  </div>
                );
              })}
            </div>
          )}

          {unsettled.length === 0 && settled_list.length === 0 && !loading && (
            <div className="card" style={{ textAlign: 'center', padding: 40, borderStyle: 'dashed' }}>
              <div style={{ fontSize: 28, marginBottom: 10 }}></div>
              <div style={{ fontWeight: 500, marginBottom: 5 }}>No entries yet</div>
              <div style={{ color: 'var(--mid)', fontSize: 13 }}>
                Use <strong>+ Entry</strong> for one-on-one IOUs or <strong>Split Split</strong> for group expenses
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Person Modal */}
      {showAddPerson && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowAddPerson(false)}>
          <div className="modal" style={{ maxWidth: 400 }}>
            <div className="modal-title">Add Person</div>
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input className="input" placeholder="e.g. Juan, Mom, Worker" value={personForm.name} onChange={e => setPersonForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Email (optional)</label>
              <input className="input" type="email" placeholder="juan@example.com" value={personForm.email} onChange={e => setPersonForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <input className="input" placeholder="Any context" value={personForm.notes} onChange={e => setPersonForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={() => setShowAddPerson(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addPerson} disabled={saving}>{saving ? 'Saving…' : 'Add Person'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {showAddEntry && (
        <div className="overlay" onClick={e => e.target === e.currentTarget && setShowAddEntry(false)}>
          <div className="modal" style={{ maxWidth: 460 }}>
            <div className="modal-title">Add IOU Entry</div>
            <div className="form-group">
              <label className="form-label">Person *</label>
              <select className="input" value={entryForm.counterpart_id} onChange={e => setEntryForm(f => ({ ...f, counterpart_id: e.target.value }))}>
                <option value="">Select person…</option>
                {summary?.counterparts?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Direction *</label>
              <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--stone)', width: 'fit-content' }}>
                {[['they_owe_me', ' They owe me'], ['i_owe_them', ' I owe them']].map(([val, label]) => (
                  <button key={val} type="button" onClick={() => setEntryForm(f => ({ ...f, direction: val }))} style={{
                    padding: '8px 16px', fontSize: 12.5, border: 'none', cursor: 'pointer',
                    background: entryForm.direction === val ? 'var(--ink)' : 'white',
                    color: entryForm.direction === val ? 'white' : 'var(--ink2)',
                    fontWeight: entryForm.direction === val ? 700 : 400,
                  }}>{label}</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Date *</label>
                <input className="input" type="date" value={entryForm.date} onChange={e => setEntryForm(f => ({ ...f, date: e.target.value }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Amount *</label>
                <CurrencyInput value={entryForm.amount} onChange={(raw, usd) => { setEntryForm(f => ({ ...f, amount: raw })); setUsdAmount(usd); }} placeholder="0.00" />
              </div>
            </div>
            <div className="form-group mt-4">
              <label className="form-label">Description *</label>
              <input className="input" placeholder="e.g. Dinner, Uber, Groceries" value={entryForm.description} onChange={e => setEntryForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <input className="input" value={entryForm.notes} onChange={e => setEntryForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div className="flex gap-3 justify-between mt-4">
              <button className="btn btn-ghost" onClick={() => setShowAddEntry(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addEntry} disabled={saving}>{saving ? 'Saving…' : 'Add Entry'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Split Modal */}
      {showSplit && (
        <SplitModal
          counterparts={summary?.counterparts || []}
          fmt={fmt}
          onClose={() => setShowSplit(false)}
          onSaved={load}
        />
      )}
    </div>
  );
}
